// src/services/assetService.ts

import { API_CONFIG } from '../config/apiConfig';

// --- INTERFACES ---
export interface AssetData {
  id: string; // This will now be the assets_id from Joget
  assetOwnerType: string;
  assetType: string;
  assetSubType: string;
  assetName: string;
  assetPostCode: string;
  assetLoan: boolean;
  assetUnit: string;
  assetUnitPrice: string;
  assetCurrentPrice: string;
  assetspurchaseDate: string;
  assetspurchaseValue: string;
  status?: string; // Status might not come from the GET list, make it optional
  familyMemberId?: string;
  jogetId?: string; // Optional: if you want to keep the original Joget ID
}

export interface AssetsApiResponse {
  status: string;
  data: AssetData[]; // Your existing AssetData interface
  pagination: {
    total: number;
    size: number;
    currentPage: number;
  };
}


// --- AUTHENTICATION HELPER ---
const getAuthToken = (): string | null => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.error('Authentication Error: JWT Token not found in localStorage.');
    return null;
  }
  return token;
};


// --- API FUNCTIONS ---

/**
 * Fetches all assets for the logged-in user.
 */
export const fetchassetsData = async (page = 1, size = 10): Promise<AssetsApiResponse> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    // Append page and size as query parameters to the URL
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/assets?page=${page}&size=${size}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(errorBody.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();

  } catch (error) {
    console.error('Error fetching assets data:', error);
    throw error;
  }
};


/**
 * Creates or updates an asset record.
 */
export const saveOrUpdateassetsRecord = async (asset: AssetData): Promise<any> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');
  const payload = {
    // Top-level fields required by the controller
    id: asset.id || null, 
    status: asset.status || 'active', // Ensure status is always sent

    // Nested request_data object
    request_data: {
      assetOwnerType: asset.assetOwnerType,
      assetType: asset.assetType,
      assetSubType: asset.assetSubType,
      assetName: asset.assetName,
      assetPostCode: asset.assetPostCode,
      assetLoan: asset.assetLoan,
      assetUnit: asset.assetUnit,
      assetUnitPrice: asset.assetUnitPrice,
      assetCurrentPrice: asset.assetCurrentPrice,
      assetspurchaseDate: asset.assetspurchaseDate,
      assetspurchaseValue: asset.assetspurchaseValue,
      // CRITICAL: Include familyMemberId if it exists on the asset object
      familyMemberId: asset.familyMemberId,
    }
  };
  
  // Clean up undefined familyMemberId before sending
  if (payload.request_data.familyMemberId === '' || payload.request_data.familyMemberId === undefined) {
      delete payload.request_data.familyMemberId;
  }

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/assets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error saving asset record:`, error);
    throw error;
  }
};


// ✅ --- ADD THIS NEW FUNCTION ---
/**
 * Fetches the list of family members (dependents) for the logged-in user.
 */
export const fetchDependents = async (): Promise<{ id: string; name: string }[]> => {
  const token = getAuthToken();
  if (!token) {
    // Throwing an error is better here as this data is critical for the form
    throw new Error('User is not authenticated.');
  }

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/profile/dependents`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(errorBody.message || 'Failed to fetch dependents');
    }

    const result = await response.json();
    return result.data || []; // Ensure it always returns an array
    
  } catch (error) {
    console.error('Error fetching dependents data:', error);
    throw error; // Re-throw the error to be caught by the component
  }
};

/**
 * Soft deletes an asset record by its ID.
 */
export const deleteassetsRecord = async (id: string): Promise<{ success: boolean; message: string }> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/assets/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return { success: true, message: result.message };
  } catch (error: any) {
    console.error('Error deleting asset record:', error);
    return { 
      success: false, 
      message: error.message || 'Failed to delete asset record.' 
    };
  }
};